import { useState } from 'react';

import circleImage from '../../../../assets/images/courses/team-players/design.png';
import introImg from '../../../../assets/images/courses/teamplayer&collaboration/chapter13.1.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { InfoBlock } from '../../InfoBlock';
import { MediaBlock } from '../../MediaBlock';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersAndCollaborationChapter13: React.FC<
  TeamPlayersAndCollaborationChapter1
> = ({ chapter, onNextChapter, totalPages }) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };

  const roadmapSteps = [
    {
      number: '01',
      text: 'Define',
      content:
        "First things first, let's get clear on what it means to be an awesome team player. What does it look like to stand out in a team? Think about the behaviors and characteristics you admire in others - like good communication, reliability, respect, and a positive attitude.",
      example:
        'Meet Sarah, a high school student who excels in her extracurricular activities. She´s always admired her peers who stand out as awesome team players, demonstrating traits like effective communication, reliability, respect, and a positive attitude. Sarah realizes that these qualities contribute to successful teamwork and wants to cultivate them within herself.',
    },
    {
      number: '02',
      text: 'Identity',
      content:
        'Now, take a moment to think about where you can improve. Reflect on what you´ve learned and identify specific areas where you want to grow. Maybe it´s better communication, meeting deadlines more reliably, or being more supportive of your teammates.',
      example:
        'Reflecting on her own behavior, Sarah acknowledges that she has room for improvement in certain areas. While she´s generally reliable and positive, she sometimes struggles with effective communication, especially during group projects. She also recognizes the importance of being more supportive of her teammates and meeting deadlines consistently.',
    },
    {
      number: '03',
      text: 'Set Goals',
      content:
        'Time to get specific! Set some clear and measurable goals that will help you improve in those areas. Whether it´s documenting accountabilities, using new tools, or just being more reliable, make sure your goals are something you can track and measure.',
      example:
        'Sarah sets specific and measurable goals to address these areas of improvement. She commits to documenting her accountabilities for each project, using communication tools to enhance team collaboration, and ensuring she meets all project deadlines without fail.',
    },
    {
      number: '04',
      text: 'Plan',
      content:
        'Now, let´s make a plan. Outline exactly what steps you´ll take to achieve those goals. What actions will you take, and when will you do them? Having a plan in place will keep you on track and focused on your goals.',
      example:
        'With her goals in mind, Sarah outlines a detailed plan of action. She schedules regular check-ins with herself to monitor her progress, allocates time each week for skill-building activities, and identifies resources such as online tutorials and workshops to support her development as a team player.',
    },
    {
      number: '05',
      text: 'Allocate',
      content:
        'Think about the time and resources you´ll need to make your plan happen. How much time can you dedicate to improving your team player skills? Who can support you along the way? And what resources might you need access to?',
      example:
        'Sarah dedicates specific time slots each week to work on her goals, making sure to balance her academic responsibilities with her extracurricular commitments. She reaches out to her teachers and mentors for guidance and support, knowing that their expertise will be invaluable on her journey.',
    },
    {
      number: '06',
      text: 'Communicate',
      content:
        'Don´t keep your goals a secret! Share them with your team and stakeholders so they know what you´re working on. You might even ask for their support and encouragement as you work towards becoming a better team player.',
      example:
        'Sarah shares her goals and action plan with her peers and teachers, seeking their encouragement and feedback. She emphasizes her commitment to becoming a better team player and invites them to hold her accountable for her progress.',
    },
    {
      number: '07',
      text: 'Accountable',
      content:
        'Hold yourself accountable for your actions and progress. Set reminders, track your progress, and learn from any mistakes along the way. You´ve got this!',
      example:
        'Taking responsibility for her actions, Sarah sets up reminders and tracking mechanisms to stay on top of her goals. She acknowledges her achievements and learns from any setbacks, using them as opportunities for growth rather than discouragement.',
    },
    {
      number: '08',
      text: 'Input',
      content:
        'Seek feedback from others on your progress. How are you doing? What could you do better? Use this feedback to guide your improvement journey.',
      example:
        'Sarah actively seeks feedback from her teammates and teachers, welcoming their insights on her progress. She listens attentively to their suggestions and incorporates them into her improvement plan, recognizing the value of diverse perspectives in her development.',
    },
    {
      number: '09',
      text: 'Reflect',
      content:
        'Finally, take some time to reflect on your progress regularly. What´s going well? What challenges have you faced, and how can you overcome them? Keep adjusting your plan as needed to keep moving forward. You´re on your way to being an amazing team player!',
      example:
        'Regularly reflecting on her journey, Sarah celebrates her successes and evaluates her challenges. She adjusts her plan as needed, remaining flexible and resilient in the face of obstacles. With each reflection, Sarah grows more confident in her abilities as a team player, knowing that she´s making meaningful strides towards her goals.',
    },
  ];
  const positions = [
    'circle-1', // 1st (Top center)
    'circle-2', // 2nd (Upper right)
    'circle-3', // 3rd (Right)
    'circle-4', // 4th (Lower right)
    'circle-5', // 5th (Bottom right)
    'circle-6', // 6th (Bottom center)
    'circle-7', // 7th (Bottom left)
    'circle-8', // 8th (Left)
    'circle-9', // 9th (Upper left)
  ];
  const zIndexOrder = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [selectStepExample, setSelectStepExample] = useState<number | null>(null);

  const handleExampleClick = (index: number) => {
    setSelectStepExample(index);
    setCompletedSteps([index]); // Reset the array to only contain the clicked step
  };

  const handleClick = (index: number) => {
    setSelectedStep(index);
    setCompletedSteps([index]); // Reset the array to only contain the clicked step
  };

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Teamwork Roadmap</h2>
        <br />
        <MediaBlock mediaImage={introImg}></MediaBlock>
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">
          Team Player Roadmap - Nine Steps
        </h3>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          This Team Player Roadmap is all about helping you reflect on what you've learned and make
          some commitments that will really stick. It's like your roadmap for becoming an even
          better team player, keeping you focused, organized, and motivated along the way.
        </p>
        <p className="text-lg font-semibold flex-1 mt-5 mb-5">
          Let us explore the roadmap's nine steps in detail.
        </p>
        <p className="text-lg font-semibold flex-1 mt-5 mb-5">
          Click on the tabs for each step definition and an example of how Sarah utilizing the Team
          Player Roadmap through each step.
        </p>
        <div className="circle-exterior pb-5 mb-0 sm:pb-16 sm:mb-24">
          <div className="relative max-w-[100%] md:max-w-[60%] mx-auto">
            <div className="relative w-[350px] h-[350px] sm:w-[450px] sm:h-[450px] mx-auto mt-10">
              {/* Center Circle */}
              <img src={circleImage} alt="Team" />
              <div className="p-4 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 center-circle text-black w-32 h-32 flex items-center justify-center rounded-full font-bold text-center text-xs">
                Team Player Roadmap
              </div>
              {/* Roadmap Steps */}
              {roadmapSteps.map((step, index) => (
                <div
                  key={step.number}
                  className={`absolute ${positions[index]} text-white w-22 h-22 flex text-center outer-circle rounded-full`}
                  style={{ zIndex: zIndexOrder[index] }}
                  onClick={() => handleClick(index)}
                >
                  <div className="relative flex items-center justify-center w-full h-full">
                    {/* Check Icon if clicked */}
                    {completedSteps.includes(index) && (
                      <span className="absolute text-lg check-active">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          height="25"
                          width="25"
                          viewBox="0 0 26 26"
                          className="fill-white"
                        >
                          <path
                            d="M12.999995,0C5.834703,0 0,5.834703 0,12.999995C0,20.165287 5.834703,25.999994 12.999995,25.999994C20.165287,25.999994 25.999994,20.165287 25.999994,12.999995C25.999994,5.834703 20.165287,0 12.999995,0z M11.256466,18.177055C11.095875,18.337641 10.889402,18.414114 10.682937,18.414114C10.484109,18.414114 10.292933,18.345291 10.132348,18.199997L4.939998,13.443525 6.033525,12.242934 10.652351,16.471764 19.905289,7.21882 21.052347,8.365879 11.256466,18.177055z"
                            fill="black"
                          />
                        </svg>
                      </span>
                    )}
                    <span className="text-2xl font-medium span-number mr-6 absolute">
                      {step.number}
                    </span>
                    <span className="text-sm span-text absolute min-w-[80px]">{step.text}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <br />
        <h2 className="font-semibold text-3xl">Activity: Your Teamwork Roadmap</h2>
        <InfoBlock
          title=""
          text="We encourage you to print or save your responses to the following steps to share with another person to help foster follow through on your efforts and plan. "
        />
        <br />
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 1. Define</h3>
        <div id="print-view" className="mb-10 mt-10">
          <p className="text-lg font-semibold flex-1 mt-5 mb-5">
            Clearly define what it means to be a standout team player.
          </p>
          <ol className=" list pl-5 mb-14 text-xl font-light">
            <li>
              <p className="text-xl font-light flex-1 mt-5 mb-5">
                <strong>
                  What does being a standout team player mean to you? What behaviors and
                  characteristics would you need to embody?
                </strong>{' '}
                This could include things like effective communication, reliability, respect, and a
                positive attitude
              </p>
              <textarea
                className="w-full  h-32 p-2 border rounded"
                placeholder="Enter your text here..."
              ></textarea>
            </li>
          </ol>

          <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 2. Identify</h3>
          <div id="print-view" className="mb-10 mt-10">
            <p className="text-lg font-semibold flex-1 mt-5 mb-5">
              Clearly define what it means to be a standout team player.
            </p>
            <ol className=" list pl-5 mb-14 text-xl font-light">
              <li>
                <p className="text-xl font-light flex-1 mt-5 mb-5">
                  <strong>
                    Take time to reflect on and then identify the specific areas in which you need
                    to improve as a team player.
                  </strong>{' '}
                  Think about what you learned through this course. What area is of most interest,
                  and what skills or qualities do you want to focus on developing further?
                </p>
                <textarea
                  className="w-full  h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </li>
            </ol>
          </div>
          <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 3. Set Goals</h3>
          <div id="print-view" className="mb-10 mt-10">
            <p className="text-lg font-semibold flex-1 mt-5 mb-5">
              Set specific and measurable goals.
            </p>
            <ol className=" list pl-5 mb-14 text-xl font-light">
              <li>
                <p className="text-xl font-light flex-1 mt-5 mb-5">
                  <strong>
                    Set specific and measurable goals that align with your areas of improvement.
                  </strong>{' '}
                  These could include "I will document accountabilities whenever assigned tasks", "I
                  will use the Context Funnel weekly to align the team behind goals", or "I will be
                  more reliable in meeting deadlines and manage expectations through daily progress
                  updates."
                </p>
                <textarea
                  className="w-full  h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </li>
            </ol>
          </div>
          <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 4. Plan</h3>
          <div id="print-view" className="mb-10 mt-10">
            <p className="text-lg font-semibold flex-1 mt-5 mb-5">
              Develop your plan of action that focusses on behaviours.
            </p>
            <ol className=" list pl-5 mb-14 text-xl font-light">
              <li>
                <p className="text-xl font-light flex-1 mt-5 mb-5">
                  Develop your specific plan of action that outlines all the steps you will take to
                  achieve your goals and become a more effective and valuable team player. What will
                  you do, and when will you do it?
                </p>
                <textarea
                  className="w-full  h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </li>
            </ol>
          </div>
          <InfoBlock
            title=""
            text="We encourage you to go into your Vero  dashboard and update your profile, set goals and track milestones. "
          />
          <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 5. Allocate</h3>
          <div id="print-view" className="mb-10 mt-10">
            <p className="text-lg font-semibold flex-1 mt-5 mb-5">Allocate time and resources.</p>
            <ol className=" list pl-5 mb-14 text-xl font-light">
              <li>
                <p className="text-xl font-light flex-1 mt-5 mb-5">
                  Allocate the time and resources you will need to complete your plan of action and
                  work on becoming a more effective and valuable team player. How much time will you
                  invest? Who can help you? What resources will you need access to? This might
                  include seeking help from your manager or completing a training course to upskill.
                </p>
                <textarea
                  className="w-full  h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </li>
            </ol>
          </div>

          <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 6. Communicate</h3>
          <div id="print-view" className="mb-10 mt-10">
            <p className="text-lg font-semibold flex-1 mt-5 mb-5">
              Communicate your goals and commitments.
            </p>
            <ol className=" list pl-5 mb-14 text-xl font-light">
              <li>
                <p className="text-xl font-light flex-1 mt-5 mb-5">
                  Communicate your goals and plan of action to your team and stakeholders, so they
                  are aware of what you're working on. Who will you communicate your goals and
                  commitments with? You might like to ask for their support on the journey.
                </p>
                <textarea
                  className="w-full  h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </li>
            </ol>
          </div>
          <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 7. Accountable</h3>
          <div id="print-view" className="mb-10 mt-10">
            <p className="text-lg font-semibold flex-1 mt-5 mb-5">Hold yourself accountable.</p>
            <ol className=" list pl-5 mb-14 text-xl font-light">
              <li>
                <p className="text-xl font-light flex-1 mt-5 mb-5">
                  How will I take responsibility and hold myself accountable for my actions, for
                  meeting my goals and taking the steps needed to improve as a team player? This
                  includes tracking your progress, setting reminders, and learning from mistakes.
                </p>
                <textarea
                  className="w-full  h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </li>
            </ol>
          </div>
          <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 8. Input</h3>
          <div id="print-view" className="mb-10 mt-10">
            <p className="text-lg font-semibold flex-1 mt-5 mb-5">
              Seek input and feedback form others.
            </p>
            <ol className=" list pl-5 mb-14 text-xl font-light">
              <li>
                <p className="text-xl font-light flex-1 mt-5 mb-5">
                  How and when will I seek feedback from my team members and management? How will I
                  use this as a guide for improvement?
                </p>
                <textarea
                  className="w-full  h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </li>
            </ol>
          </div>
          <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Step 9. Conclusion</h3>
          <div id="print-view" className="mb-10 mt-10">
            <p className="text-lg font-semibold flex-1 mt-5 mb-5">Reflect and adjust your plan.</p>
            <ol className=" list pl-5 mb-14 text-xl font-light">
              <li>
                <p className="text-xl font-light flex-1 mt-5 mb-5">
                  Reflect regularly on your progress and adjust your plan as needed to ensure that
                  you are making steady progress towards becoming a better team player. How am I
                  progressing? What is coming most naturally to me? What challenges have I faced and
                  how will I overcome them? What support can I seek?
                </p>
                <textarea
                  className="w-full  h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </li>
            </ol>
          </div>

          <div className="mt-5 text-right">
            <button onClick={handlePrint} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>

        <br />
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Conclusion
        </button>
      </div>
      {/* Popup for Selected Step */}
      {selectedStep !== null && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white  rounded-lg shadow-lg w-96 text-center">
            {selectStepExample !== null ? (
              <div>
                <div className="w-full bg-[#394d63] text-white">
                  {' '}
                  <h2 className="text-lg font-bold p-2  mb-2">
                    Example: {roadmapSteps[selectedStep].text}
                  </h2>
                </div>{' '}
                <p className="text-gray-700 p-4">{roadmapSteps[selectedStep].content}</p>
              </div>
            ) : (
              <div>
                <div className="bg-[#394d63] w-full text-white">
                  {' '}
                  <h2 className="text-lg font-bold p-2  mb-2">{roadmapSteps[selectedStep].text}</h2>
                </div>{' '}
                <p className="text-gray-700 p-4">{roadmapSteps[selectedStep].content}</p>
                <div className="flex flex-col items-center mt-4">
                  <button
                    onClick={() => handleExampleClick(selectedStep)}
                    className="mt-4 px-2 py-1 bg-[#394d63] text-white rounded"
                  >
                    Example
                  </button>
                </div>
              </div>
            )}
            <div className="m-2 ">
              <button
                onClick={() => {
                  setSelectedStep(null), setSelectStepExample(null);
                }}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
